import React from 'react';
import { motion } from 'framer-motion';
import { LuUsers as Users, LuZap as Zap, LuTrendingUp as TrendingUp } from 'react-icons/lu';
import { containerVariants, cardVariants } from '../../lib/animations';

const valueProps = [
  {
    icon: Users,
    headline: 'Dedicated Design Support',
    description: 'We embed as part of your team - not just another agency project. Weekly or bi-weekly deliverables and reviews keep you moving fast.'
  },
  {
    icon: Zap,
    headline: 'Proprietary Arabica Framework',
    description: 'Our battle-tested methodology combines UI/UX excellence with conversion optimization, specifically built for startup velocity.'
  },
  {
    icon: TrendingUp,
    headline: 'Built for E-commerce, Fintech & AI',
    description: 'Deep vertical expertise where design directly impacts revenue, user trust, and product adoption.'
  }
];

const ValueProposition = () => {
  return (
    <section className="py-16 md:py-32 bg-black relative overflow-hidden">
      <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full filter blur-3xl" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-white/5 rounded-full filter blur-3xl" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <motion.div
          className="text-center mb-12 md:mb-20"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="inline-block px-4 py-2 bg-white/5 text-white rounded-full text-sm font-semibold mb-4 border border-white/10"
          >
            Why Arabica
          </motion.div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 md:mb-6">
            Design Partnership Built for Startup Speed
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-neutral-400 max-w-3xl mx-auto">
            We don't just deliver designs - we become part of your growth engine
          </p>
        </motion.div>

        <motion.div
          className="grid md:grid-cols-3 gap-6 md:gap-8 max-w-7xl mx-auto"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          {valueProps.map((prop, index) => (
            <motion.div
              key={index}
              className="group relative p-6 md:p-8 rounded-3xl border border-white/10 bg-white/5 hover:bg-white/10 hover:shadow-2xl transition-all duration-500 overflow-hidden backdrop-blur-sm"
              variants={cardVariants}
              whileHover={{ y: -12, scale: 1.02 }}
            >
              <motion.div
                className="w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-white/10 flex items-center justify-center mb-6 md:mb-8 shadow-lg group-hover:bg-white/20 transition-all backdrop-blur-sm"
                whileInView={{ scale: [0, 1.2, 1], rotate: [0, 10, 0] }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <prop.icon className="w-8 h-8 md:w-10 md:h-10 text-white" />
              </motion.div>
              <h3 className="text-xl md:text-2xl font-bold text-white mb-3 md:mb-4 group-hover:text-neutral-100 transition-colors">
                {prop.headline}
              </h3>
              <p className="text-neutral-400 leading-relaxed text-base md:text-lg">
                {prop.description}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default ValueProposition;